import { Fragment, useCallback, useEffect, useMemo, useState } from "react";
import {
  fetchFoodManualRefreshWorkflowStatus,
  postFoodManualRefreshWorkflowStepApply,
  postFoodManualRefreshWorkflowStepDryRun,
  postFoodManualRefreshWorkflowStepValidate,
} from "../../../../api/foodApi";
import { formatRelativeFreshness } from "../../../../utils/foodFormatters";

const STATUS_CLASS = {
  NOT_STARTED: "food-manual-refresh-status--not-started",
  READY: "food-manual-refresh-status--ready",
  DRY_RUN_REQUIRED: "food-manual-refresh-status--dry-run-required",
  DRY_RUN_PASSED: "food-manual-refresh-status--dry-run-passed",
  DRY_RUN_FAILED: "food-manual-refresh-status--failed",
  READY_TO_APPLY: "food-manual-refresh-status--ready-to-apply",
  APPLY_RUNNING: "food-manual-refresh-status--running",
  APPLY_SUCCEEDED: "food-manual-refresh-status--current",
  APPLY_FAILED: "food-manual-refresh-status--failed",
  NEEDS_REVIEW: "food-manual-refresh-status--needs-review",
  STALE: "food-manual-refresh-status--stale",
  BLOCKED: "food-manual-refresh-status--blocked",
  SKIPPED: "food-manual-refresh-status--skipped",
  CURRENT: "food-manual-refresh-status--current",
};

const APPLY_CONFIRM_MESSAGES = {
  OSM_INGEST:
    "You are about to run live OSM ingest. This may insert or update supermarket rows. Continue?",
  OSM_NORMALIZATION:
    "You are about to apply OSM normalization/dedupe changes. Ambiguous records will remain in the review queue. Continue?",
  CHAIN_LOCATOR_IMPORT:
    "You are about to import chain branch locator rows from the selected file. Continue?",
  STORE_RECONCILIATION:
    "You are about to apply high-confidence OSM + chain matches only. Conflicts will remain in review. Continue?",
  ACCESS_RECALCULATION:
    "You are about to recalculate Food Access Areas using the current trusted store dataset. Continue?",
};

const VALIDATE_STEPS = new Set([
  "PREFLIGHT_STATUS",
  "STORE_REVIEW_QUEUE",
  "REVIEW_CONFLICT_CHECK",
  "COVERAGE_VALIDATION",
  "FINAL_SNAPSHOT",
]);

function parseWorkflowError(error) {
  if (error?.body?.error === "RUN_ALREADY_IN_PROGRESS") {
    return error.body.message || "A conflicting Food Access operation is already running.";
  }
  if (error?.body?.error === "DRY_RUN_REQUIRED") {
    return error.body.message || "Run a successful dry run before applying.";
  }
  if (error?.body?.error === "CONFIRMATION_REQUIRED") {
    return error.body.message || "Apply requires confirmation.";
  }
  return error?.body?.message || error?.message || "Workflow action failed.";
}

function formatSummary(summary = {}) {
  if (!summary || typeof summary !== "object") return "—";
  const entries = Object.entries(summary).filter(([, value]) => value != null && value !== "");
  if (!entries.length) return "—";
  return entries
    .slice(0, 4)
    .map(([key, value]) => `${key}: ${typeof value === "object" ? "…" : value}`)
    .join(" · ");
}

const FoodManualRefreshPlaybookSection = ({
  canManage = false,
  geography = {},
  onWorkflowUpdated,
}) => {
  const [workflow, setWorkflow] = useState(null);
  const [loading, setLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState("");
  const [activeAction, setActiveAction] = useState(null);
  const [expandedStepKey, setExpandedStepKey] = useState(null);
  const [expandedHistoryId, setExpandedHistoryId] = useState(null);
  const [lastActionResult, setLastActionResult] = useState(null);

  const geographyPayload = useMemo(
    () => ({
      ...geography,
      includeAdminLinks: true,
    }),
    [geography]
  );

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const payload = await fetchFoodManualRefreshWorkflowStatus(geographyPayload);
      setWorkflow(payload);
      setErrorMessage("");
      onWorkflowUpdated?.(payload);
    } catch (error) {
      setErrorMessage(parseWorkflowError(error));
    } finally {
      setLoading(false);
    }
  }, [geographyPayload, onWorkflowUpdated]);

  useEffect(() => {
    load();
  }, [load]);

  const runAction = async (stepKey, actionType) => {
    if (!canManage || activeAction) return;

    const step = workflow?.steps?.find((entry) => entry.stepKey === stepKey);
    if (!step) return;

    if (actionType === "APPLY") {
      const message = APPLY_CONFIRM_MESSAGES[stepKey];
      if (message && !window.confirm(message)) return;

      const needsAck =
        stepKey === "ACCESS_RECALCULATION" &&
        (workflow?.thresholds?.reviewQueueHigh ?? 25) <= (step.summary?.reviewQueueCount ?? 0);
      if (needsAck) {
        const ack = window.confirm(
          "Review queue is above the configured threshold. Acknowledge and continue anyway?"
        );
        if (!ack) return;
      }
    }

    try {
      setActiveAction({ stepKey, actionType });
      setErrorMessage("");

      const payload = { ...geographyPayload };
      if (actionType === "APPLY") {
        payload.confirm = true;
        if (stepKey === "ACCESS_RECALCULATION") {
          payload.acknowledgeReviewBacklog = true;
        }
      }

      let result;
      if (actionType === "DRY_RUN") {
        result = await postFoodManualRefreshWorkflowStepDryRun(stepKey, payload);
      } else if (actionType === "APPLY") {
        result = await postFoodManualRefreshWorkflowStepApply(stepKey, payload);
      } else {
        result = await postFoodManualRefreshWorkflowStepValidate(stepKey, payload);
      }

      setLastActionResult(result);
      await load();
    } catch (error) {
      setErrorMessage(parseWorkflowError(error));
    } finally {
      setActiveAction(null);
    }
  };

  const scrollToStep = (stepKey) => {
    const element = document.getElementById(`food-manual-refresh-step-${stepKey}`);
    element?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  useEffect(() => {
    if (window.location.hash === "#food-manual-refresh-playbook" && workflow?.currentStepKey) {
      scrollToStep(workflow.currentStepKey);
    }
  }, [workflow?.currentStepKey]);

  const actionBusy = Boolean(activeAction);

  return (
    <section
      id="food-manual-refresh-playbook"
      className="panel-card food-manual-refresh-playbook"
      aria-labelledby="food-manual-refresh-playbook-title"
    >
      <div className="food-manual-refresh-playbook__header">
        <div>
          <h2 id="food-manual-refresh-playbook-title">Manual Refresh Playbook</h2>
          <p className="muted">
            Dry-run-first operational sequence for OSM ingest through access recalculation. No
            scheduler — each step requires explicit Admin action.
          </p>
        </div>
        {workflow?.overallStatus ? (
          <span
            className={`food-manual-refresh-overall food-manual-refresh-overall--${workflow.overallStatus.toLowerCase()}`}
          >
            {workflow.overallStatus.replace(/_/g, " ")}
          </span>
        ) : null}
      </div>

      {errorMessage ? (
        <p className="food-dashboard__error" role="alert">
          {errorMessage}
        </p>
      ) : null}

      {loading && !workflow ? (
        <p className="muted">Loading manual refresh workflow…</p>
      ) : workflow ? (
        <>
          {workflow.nextRecommendedAction?.label ? (
            <div className="food-manual-refresh-playbook__next-action" role="status">
              <span className="muted">Next recommended action</span>
              <p>
                <strong>{workflow.nextRecommendedAction.label}</strong>
              </p>
              {workflow.nextRecommendedAction.stepKey ? (
                <button
                  type="button"
                  className="btn btn--ghost btn--sm"
                  onClick={() => scrollToStep(workflow.nextRecommendedAction.stepKey)}
                >
                  Go to step
                </button>
              ) : null}
            </div>
          ) : null}

          <ol className="food-manual-refresh-playbook__steps">
            {workflow.steps.map((step, index) => {
              const isExpanded = expandedStepKey === step.stepKey;
              const isRunning =
                activeAction?.stepKey === step.stepKey ||
                step.status === "APPLY_RUNNING";
              const statusClass = STATUS_CLASS[step.status] || "food-manual-refresh-status--ready";

              return (
                <li
                  key={step.stepKey}
                  id={`food-manual-refresh-step-${step.stepKey}`}
                  className={`food-manual-refresh-playbook__step${step.stepKey === workflow.currentStepKey ? " food-manual-refresh-playbook__step--current" : ""}`}
                >
                  <div className="food-manual-refresh-playbook__step-header">
                    <strong>
                      {index + 1}. {step.label}
                    </strong>
                    <span className={`food-manual-refresh-status ${statusClass}`}>
                      {step.statusLabel || step.status}
                    </span>
                  </div>

                  {step.description ? <p className="muted">{step.description}</p> : null}

                  {step.blockedReason ? (
                    <p className="food-manual-refresh-playbook__blocked" role="status">
                      Blocked: {step.blockedReason}
                    </p>
                  ) : null}
                  {step.staleReason ? (
                    <p className="food-manual-refresh-playbook__stale" role="status">
                      Stale: {step.staleReason}
                    </p>
                  ) : null}

                  <p className="muted">{step.nextAction}</p>

                  {(step.warnings?.length || step.errors?.length) ? (
                    <ul className="food-manual-refresh-playbook__messages">
                      {step.warnings?.map((warning) => (
                        <li key={`w-${warning}`} className="food-manual-refresh-playbook__warning">
                          {warning}
                        </li>
                      ))}
                      {step.errors?.map((entry) => (
                        <li key={`e-${entry.message || entry}`} className="food-manual-refresh-playbook__error">
                          {entry.message || entry}
                        </li>
                      ))}
                    </ul>
                  ) : null}

                  <div className="food-manual-refresh-playbook__meta muted">
                    {step.lastDryRunAt ? (
                      <span>Dry run {formatRelativeFreshness(step.lastDryRunAt)}</span>
                    ) : null}
                    {step.lastRealRunAt ? (
                      <span>Apply {formatRelativeFreshness(step.lastRealRunAt)}</span>
                    ) : null}
                    {step.latestResultStatus ? (
                      <span>Latest result {step.latestResultStatus}</span>
                    ) : null}
                  </div>

                  <div className="food-normalization-page__control-row">
                    {step.dryRunAvailable ? (
                      <button
                        type="button"
                        className="btn btn--ghost btn--sm"
                        disabled={!canManage || !step.canRunDryRun || actionBusy}
                        onClick={() => runAction(step.stepKey, "DRY_RUN")}
                      >
                        {isRunning && activeAction?.actionType === "DRY_RUN"
                          ? "Running dry run…"
                          : "Run Dry Run"}
                      </button>
                    ) : null}
                    {step.realRunAvailable ? (
                      <button
                        type="button"
                        className="btn btn--primary btn--sm"
                        disabled={!canManage || !step.canRunRealRun || actionBusy}
                        onClick={() => runAction(step.stepKey, "APPLY")}
                      >
                        {isRunning && activeAction?.actionType === "APPLY"
                          ? "Applying…"
                          : "Apply / Run Real"}
                      </button>
                    ) : null}
                    {VALIDATE_STEPS.has(step.stepKey) ? (
                      <button
                        type="button"
                        className="btn btn--ghost btn--sm"
                        disabled={!canManage || actionBusy}
                        onClick={() => runAction(step.stepKey, "VALIDATE")}
                      >
                        {isRunning && activeAction?.actionType === "VALIDATE"
                          ? "Validating…"
                          : "Validate"}
                      </button>
                    ) : null}
                    <button
                      type="button"
                      className="btn btn--ghost btn--sm"
                      onClick={() =>
                        setExpandedStepKey((current) =>
                          current === step.stepKey ? null : step.stepKey
                        )
                      }
                    >
                      {isExpanded ? "Hide Details" : "View Details"}
                    </button>
                    {step.adminPath ? (
                      <a href={step.adminPath} className="btn btn--ghost btn--sm">
                        Open Tool
                      </a>
                    ) : null}
                  </div>

                  {isExpanded ? (
                    <pre className="food-normalization-page__summary">
                      {JSON.stringify(step.summary || {}, null, 2)}
                    </pre>
                  ) : null}
                </li>
              );
            })}
          </ol>

          {lastActionResult ? (
            <div className="food-manual-refresh-playbook__last-result">
              <h3>Latest action result</h3>
              <p>
                {lastActionResult.stepKey} · {lastActionResult.actionType} ·{" "}
                {lastActionResult.status}
              </p>
              <p className="muted">{lastActionResult.nextAction}</p>
              <pre className="food-normalization-page__summary">
                {JSON.stringify(lastActionResult.summary || {}, null, 2)}
              </pre>
            </div>
          ) : null}

          <div className="food-manual-refresh-playbook__history">
            <h3>Workflow action history</h3>
            {workflow.history?.length ? (
              <table className="admin-table">
                <thead>
                  <tr>
                    <th>Step</th>
                    <th>Action</th>
                    <th>Status</th>
                    <th>Triggered by</th>
                    <th>Started</th>
                    <th>Duration</th>
                    <th>Summary</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {workflow.history.map((run) => (
                    <Fragment key={run.id}>
                      <tr>
                        <td>{run.stepKey}</td>
                        <td>{run.actionType}</td>
                        <td>{run.status}</td>
                        <td>{run.triggeredBy || "—"}</td>
                        <td>{formatRelativeFreshness(run.startedAt)}</td>
                        <td>{run.durationMs != null ? `${run.durationMs} ms` : "—"}</td>
                        <td>{formatSummary(run.summary?.summary || run.summary)}</td>
                        <td>
                          <button
                            type="button"
                            className="btn btn--ghost btn--sm"
                            onClick={() =>
                              setExpandedHistoryId((current) =>
                                current === run.id ? null : run.id
                              )
                            }
                          >
                            {expandedHistoryId === run.id ? "Hide" : "Details"}
                          </button>
                        </td>
                      </tr>
                      {expandedHistoryId === run.id ? (
                        <tr>
                          <td colSpan={8}>
                            <pre className="food-normalization-page__summary">
                              {JSON.stringify(run, null, 2)}
                            </pre>
                          </td>
                        </tr>
                      ) : null}
                    </Fragment>
                  ))}
                </tbody>
              </table>
            ) : (
              <p className="muted">No manual refresh workflow actions recorded yet.</p>
            )}
          </div>
        </>
      ) : (
        <p className="muted">Unable to load manual refresh workflow.</p>
      )}
    </section>
  );
};

export default FoodManualRefreshPlaybookSection;
